import { buildAuthHeaders } from './session';
import type { MessageRatingValue } from '../types';

export interface FeedbackPayload {
  /** Current chat session id (links score to the whole session). */
  sessionId?: string;
  /** Trace id from the rated message (links score to that message). */
  traceId?: string;
  /** Star rating, integer 1-5. */
  rating: MessageRatingValue;
  /** Optional free-text comment. */
  comment?: string;
  /** Optional custom tags, defined in the block settings. */
  tags?: string[];
}

/**
 * Submit a rating for a single assistant response.
 *
 * Posts to the configured feedback endpoint (block setting) using the score
 * API contract: { traceId?, sessionId?, rating, comment?, tags? }. Throws on a
 * missing endpoint or a non-2xx response so the caller can surface an error.
 */
export async function submitFeedback(
  endpoint: string,
  payload: FeedbackPayload,
): Promise<void> {
  const url = endpoint.trim();
  if (!url) {
    throw new Error('Feedback endpoint is not configured.');
  }

  const tags = payload.tags
    ?.map((tag) => tag.trim())
    .filter((tag) => tag !== '');

  const res = await fetch(url, {
    method: 'POST',
    headers: buildAuthHeaders(),
    body: JSON.stringify({
      traceId: payload.traceId || undefined,
      sessionId: payload.sessionId || undefined,
      rating: payload.rating,
      comment: payload.comment?.trim() || undefined,
      tags: tags && tags.length > 0 ? tags : undefined,
    }),
  });

  if (!res.ok) {
    throw new Error(`Feedback request failed with status ${res.status}`);
  }
}
